import type { QueryClient } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";
import { apiQueryFn } from "./queryFetcher";
import SessionService from "../services/SessionService";
import { audienceForRoles, Audience } from "../components/Access/audience";
import type { UserIdentity } from "../hooks/useCerberus";

/**
 * Identity, readable from outside React.
 *
 * `/identity` is the one request every other one waits behind: it decides which portal the user is
 * in, so a loader cannot know whether it may fetch until it has resolved. It therefore lives in the
 * query cache under a single key, and everything that needs it — the root route loader, the page
 * loaders, `CerberusProvider` — goes through `identityQuery`. Because they share the key, whoever
 * asks first makes the request and the rest join the same promise; there is exactly one `/identity`
 * call per load, however many of them ask.
 */

/**
 * The one definition of the identity request.
 *
 * `retry: false` overrides the client default on purpose. A failed `/identity` is not a page that
 * can be shown again in a moment — `CerberusProvider` responds by clearing the session — so two
 * backed-off retries would only delay the sign-out, and would keep the app rendering nothing while
 * they ran.
 */
export const identityQuery = {
  queryKey: queryKeys.identity,
  queryFn: () => apiQueryFn<UserIdentity>("/identity"),
  retry: false,
};

/**
 * Resolve identity for a caller that runs outside React.
 *
 * Returns the cached entry, joins an in-flight request, or makes one. Signed out it resolves to
 * `null` without a request — `/identity` requires the session cookie and can only 401.
 *
 * It never throws. A loader that let this reject would replace the page with an error boundary for
 * something the app already handles: a 401 is the shared client's business (it clears the session
 * and redirects), and any other failure is `CerberusProvider`'s, which reports it and signs the
 * user out. Either way a loader's job here is only to stop early.
 */
export async function ensureIdentity(
  client: QueryClient,
): Promise<UserIdentity | null> {
  if (!SessionService.getInstance().isAuthenticated()) return null;
  try {
    return await client.ensureQueryData(identityQuery);
  } catch {
    return null;
  }
}

/** Record an identity fetched elsewhere (the signed-out case, which makes no request). */
export function publishIdentity(
  client: QueryClient,
  identity: UserIdentity | null,
): void {
  client.setQueryData(queryKeys.identity, identity);
}

/**
 * Apply a local change to the cached identity, so the cache and the provider's state stay the same
 * answer. Without this, marking a user registered (or completing a required action) would update
 * React state while the cache still held the pre-change copy, and the next reader would disagree.
 */
export function patchIdentity(
  client: QueryClient,
  patch:
    | Partial<UserIdentity>
    | ((prev: UserIdentity) => Partial<UserIdentity>),
): void {
  client.setQueryData<UserIdentity | null>(queryKeys.identity, (prev) =>
    prev
      ? { ...prev, ...(typeof patch === "function" ? patch(prev) : patch) }
      : prev,
  );
}

/** Discard the cached identity, so the next read re-fetches it. */
export function forgetIdentity(client: QueryClient): void {
  client.removeQueries({ queryKey: queryKeys.identity });
}

/**
 * The signed-in user's audience, or null when identity has not arrived yet — which is the normal
 * state during a cold load, since loaders run before the provider's fetch resolves.
 */
export function readAudience(client: QueryClient): Audience | null {
  const identity = client.getQueryData<UserIdentity | null>(queryKeys.identity);
  return audienceForRoles(identity?.roles);
}

/** The signed-in user's roles, for the same page-data context. */
export function readRoles(client: QueryClient): string[] | undefined {
  return client.getQueryData<UserIdentity | null>(queryKeys.identity)?.roles;
}

/** The signed-in user's id, for the page-data context a loader or prefetch passes along. */
export function readUserId(client: QueryClient): string | undefined {
  return client.getQueryData<UserIdentity | null>(queryKeys.identity)?.id;
}
